/**
 * Fuente única de verdad del control de acceso por módulo y rol.
 *
 * La consumen, a la vez:
 *  - Los guards de ruta en `src/router/index.js` (vía `hasAnyPermissionGuard`).
 *  - Los menús switcher entre módulos (`NavBar.vue`, `MenuCliente.vue`).
 *
 * Cada clave es un módulo y su valor es la lista de grupos que pueden entrar
 * (basta con pertenecer a uno). Un módulo que NO aparezca aquí no tiene
 * restricción de rol: solo requiere sesión autenticada (p.ej. pbxCloud,
 * system-updates y los módulos legacy /crm, /reportesCrm, /webMeetings).
 */
import { encryptStorage } from '@/utils/storage.js';

export const MODULE_ACCESS = {
  fxCrm: ['COMERCIAL', 'COMERCIAL CRM', 'ADMINISTRADOR CRM', 'COORDINADOR CRM'],
  admin: ['ADMINISTRADOR EMPRESA', 'ADMINISTRADOR SIPMOVIL'],
  adminJoin: ['ADMINISTRADOR EMPRESA', 'ADMINISTRADOR SIPMOVIL'],
  SipMovil: ['ADMINISTRADOR SIPMOVIL'],
  reportes: [
    'ADMINISTRADOR EMPRESA',
    'GERENTE EMPRESA',
    'ADMINISTRADOR SIPMOVIL',
    'COMERCIAL',
    'ADMINISTRADOR CRM',
  ],
  // reportesV2: en construcción en paralelo con `reportes`. Mismos grupos.
  reportesV2: [
    'ADMINISTRADOR EMPRESA',
    'GERENTE EMPRESA',
    'ADMINISTRADOR SIPMOVIL',
    'COMERCIAL',
    'ADMINISTRADOR CRM',
  ],
  reportesJoin: [
    'ADMINISTRADOR EMPRESA',
    'GERENTE EMPRESA',
    'ADMINISTRADOR SIPMOVIL',
    'COMERCIAL',
    'ADMINISTRADOR CRM',
  ],
  // Fexería: el rol fino (configurar / solo consultar) sale de GET /api/fexeria/access/.
  fexeria: ['ADMINISTRADOR SIPMOVIL', 'ADMINISTRADOR EMPRESA', 'OPERADOR FEXERIA'],
  // Herramienta externa FexGo (fex-1184/1186): Administrador FexGo ve ambas,
  // Operador FexGo ve solo FexGo.
  fexGo: ['ADMINISTRADOR FEXGO', 'OPERADOR FEXGO'],
  // Nota: el tile "Reportes CRM" del switcher NO usa MODULE_ACCESS porque su
  // regla incluye un flag (no solo grupos): ver canAccessCrmReportsWith().
};

/**
 * Acceso a SUB-RUTAS que requieren un rol más fino que el del módulo padre.
 * El guard del módulo (MODULE_ACCESS) solo controla la entrada al módulo; estas
 * sub-rutas necesitan además un rol concreto, si no se redirige a `redirect`.
 *
 * Forma: { [routeName]: { roles: string[], flag?: string, redirect?: string } }
 *  - roles: basta pertenecer a uno de estos grupos.
 *  - flag:  además se permite si `userInfo[flag]` es truthy (p.ej. can_create_crm_reports).
 *  - redirect: ruta destino si no cumple (default 'pbxCloud').
 */
const FXCRM_CONFIG_ROUTES = [
  'fx-need',
  'fx-origin',
  'fx-businessLine',
  'fx-businessLineHome',
  'fx-variable',
  'fx-varType',
  'fx-fxStatus',
  'fx-interactionStatus',
  'fx-fxPhase',
  'fx-fxStage',
  'fx-fxDiscardReason',
  'fx-currency',
  'fx-fxRetoma',
  'fx-tipificacion',
  'fx-emailAccount',
  'fx-zone',
  'fx-lostReason',
  'fx-sector',
  'fx-fxUsersAdmin',
  'fx-fxGroups',
  'fx-reports',
  'fx-fxBulkUpload',
  'fx-rules',
  'fx-formula',
  'fx-campaign',
  'fx-fqIntegration',
  'fx-fxFexiaSuggestions',
  // Log de auditoría: hasta 2026-09-24 no estaba en la lista y cualquier
  // usuario del CRM lo abría por URL.
  'fx-transactions',
  'fx-fxAccessTokens',
  'fx-fxFormBuilder',
  // Secciones con tabs internos (FxConfigSection.vue).
  'fx-config-general',
  'fx-config-system',
];

// Reportes de fxCrm: ADMINISTRADOR CRM o flag can_create_crm_reports (misma
// regla que la visibilidad del menú en FxCrmLayout.vue).
const FXCRM_REPORT_ROUTES = [
  'fxReport-reportsList',
  'fxReport-reportDetail',
  'fxReport-interactionByStatus',
  'fxReport-generalSales',
  'fxReport-generalManagement1',
  'fxReport-generalManagement2',
  'fxReport-generalCoordinator1',
  'fxReport-generalCoordinator2',
];

export const ROUTE_ACCESS = {
  // Panel "Gestión de mi equipo": supervisa la cartera de otras personas, así
  // que no lo ve un asesor.
  'fx-team-panel': {
    roles: ['ADMINISTRADOR CRM', 'COORDINADOR CRM'],
    redirect: 'fx-home',
  },
  // "Bandeja": transferir (admin) o repartir (coordinador) lo que llega sin
  // responsable. Un asesor no reparte nada.
  'fx-inbox': {
    roles: ['ADMINISTRADOR CRM', 'COORDINADOR CRM'],
    redirect: 'fx-home',
  },
  // "Solicitudes de aprobación": resolver descuentos y condiciones que piden
  // los gestores. El gestor pide desde la interacción, pero no resuelve.
  'fx-approvals': {
    roles: ['ADMINISTRADOR CRM', 'COORDINADOR CRM'],
    redirect: 'fx-home',
  },
  // "Historial de interacciones": los tres roles; el backend recorta lo que ve
  // cada uno. `COMERCIAL` es el grupo heredado del agente (ver `fxCrm` arriba).
  'fx-interaction-history': {
    roles: ['ADMINISTRADOR CRM', 'COORDINADOR CRM', 'COMERCIAL CRM', 'COMERCIAL'],
    redirect: 'fx-home',
  },
  ...Object.fromEntries(
    FXCRM_CONFIG_ROUTES.map((name) => [
      name,
      { roles: ['ADMINISTRADOR CRM'], redirect: 'fx-home' },
    ]),
  ),
  ...Object.fromEntries(
    FXCRM_REPORT_ROUTES.map((name) => [
      name,
      { roles: ['ADMINISTRADOR CRM'], flag: 'can_create_crm_reports', redirect: 'fx-home' },
    ]),
  ),
  // Grabaciones reutiliza Admin > Herramientas: mismo rol que exige esa ruta.
  'reportesV2-grabaciones': { roles: ['ADMINISTRADOR EMPRESA', 'ADMINISTRADOR SIPMOVIL'], redirect: 'reportesV2-dashboard' },
};

/**
 * Evalúa una entrada de ROUTE_ACCESS contra el usuario actual (lee storage).
 * @param {{roles?: string[], flag?: string}} access
 * @returns {boolean}
 */
export const meetsAccess = (access) => {
  if (!access) return true;
  const user = encryptStorage.getItem('user');
  const groups = user?.groups || [];
  if ((access.roles || []).some((role) => groups.includes(role))) return true;
  if (access.flag && user?.[access.flag]) return true;
  return false;
};

/**
 * Comprueba acceso contra una lista de grupos ya disponible (uso REACTIVO en
 * componentes Vue: pasar `this.userInfo?.groups`).
 * @param {string[]} groups - grupos del usuario.
 * @param {string} moduleKey - clave en MODULE_ACCESS.
 * @returns {boolean}
 */
export const canAccessWith = (groups = [], moduleKey) => {
  const allowed = MODULE_ACCESS[moduleKey];
  if (!allowed) return true; // sin restricción de rol → solo requiere auth
  return allowed.some((role) => groups.includes(role));
};

/**
 * Visibilidad/acceso a Reportes CRM (tile del switcher de módulos).
 * Misma regla que el sidebar y el guard de ruta: ADMINISTRADOR CRM o el flag
 * `can_create_crm_reports`. Uso REACTIVO en componentes: pasar `this.userInfo`.
 * @param {{groups?: string[], can_create_crm_reports?: boolean}} userInfo
 * @returns {boolean}
 */
export const canAccessCrmReportsWith = (userInfo) => {
  const groups = userInfo?.groups || [];
  return groups.includes('ADMINISTRADOR CRM') || !!userInfo?.can_create_crm_reports;
};

/**
 * Grupos del usuario leídos desde el almacenamiento cifrado.
 * Para uso NO reactivo (guards de ruta).
 * @returns {string[]}
 */
export const userGroups = () => {
  const user = encryptStorage.getItem('user');
  return user?.groups || [];
};

/**
 * Comprueba acceso leyendo los grupos del storage (uso en guards).
 * @param {string} moduleKey - clave en MODULE_ACCESS.
 * @returns {boolean}
 */
export const canAccess = (moduleKey) => canAccessWith(userGroups(), moduleKey);

export default MODULE_ACCESS;
